import { z } from "zod";
import {
  createApiClient,
  handleGetRequest,
  handlePostRequest,
} from "../apiService";
import { useToken } from "../tokenUtils";

import {
  addAuthorTypes,
  addDeclarationsTypes,
  addFileTypes,
  addSuggestedReviewerTypes,
  applyAuthorChangesTypes,
  authorHistoryTypes,
  Authors,
  authortype,
  createAuthorTypes,
  deleteFileTypes,
  updateFileVisibilityTypes,
  editAuthorTypes,
  fileTypes,
  putAuthorContibutionsTypes,
  removeAuthorTypes,
  setAuthorsOrderTypes,
  setMainAuthortypes,
  SuccessType,
  suggestedReviewerTypes,
  veredictsHistoryTypes,
  updateReviewFileVisibilityTypes,
  editSuggestedReviewerTypes,
  articleAuthorTypes,
} from "./authorTypes";
import { ArticleType, sendArticleType } from "../articles/articlesTypes";
import { getSubdomain } from "../../utils/getSubdomain/subdomain";

export const authorSchema = z.object({
  orcid: z.string().optional(),
  first_name: z.string().min(1, "El nombre es obligatorio"),
  middle_name: z.string().optional(),
  last_name: z.string().min(1, "El apellido es obligatorio"),
  affiliation: z.string().min(1, "La institución es obligatoria"),
  user_login: z.string().email("El email no es válido"),
});

const domain = getSubdomain();
const apiClient = createApiClient("");

const getAuthors = async (): Promise<Authors> => {
  const currentToken = useToken();
  const author = await handleGetRequest<Authors>(
    apiClient,
    `${domain}/author/`,
    currentToken,
  );
  return author;
};

const handleRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const handlePutRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const handlePutFileRequest = async (
  url: string,
  data: fileTypes | FormData | File,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const handlePostFileRequest = async (
  url: string,
  data: fileTypes | FormData | File,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const sendArticle = async (
  article: sendArticleType,
): Promise<{
  article: ArticleType;
  article_slug: string;
  authors: articleAuthorTypes[];
}> => {
  const currentToken = useToken();
  const res = await handlePostRequest<{
    article: ArticleType;
    article_slug: string;
    authors: articleAuthorTypes[];
  }>(
    apiClient,
    `${domain}/author/articles`,
    JSON.stringify(article),
    currentToken,
  );
  return res as {
    article: ArticleType;
    article_slug: string;
    authors: articleAuthorTypes[];
  };
};

const getAuthorMe = async (): Promise<authortype> => {
  const currentToken = useToken();
  const res = await apiClient.get(`${domain}/author/me`, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });
  return res.data as authortype;
};

const getMyArticles = async (): Promise<ArticleType[]> => {
  const currentToken = useToken();
  const res = await apiClient.get(`${domain}/author/articles`, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });
  return res.data as ArticleType[];
};

const createAuthor = async (data: createAuthorTypes): Promise<string> => {
  const currentToken = useToken();

  const res = await handleRequest(`${domain}/author/`, data, currentToken);
  return res as string;
};

const addAuthor = async (
  data: addAuthorTypes,
): Promise<articleAuthorTypes[]> => {
  const currentToken = useToken();
  const path = `${domain}/author/${data.author_slug}/articles/${data.article_slug}`;
  const res = await handlePutRequest(path, data.contributions, currentToken);
  return res;
};

const setMainAuthor = async (
  data: setMainAuthortypes,
): Promise<articleAuthorTypes[]> => {
  if (!data.article_slug || !data.author_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const currentToken = useToken();
  const path = `${domain}/author/${data.author_slug}/articles/${data.article_slug}/main`;
  const res = await handlePutRequest(path, data, currentToken);
  return res as articleAuthorTypes[];
};

const setMailingAuthor = async (
  data: setMainAuthortypes,
): Promise<articleAuthorTypes[]> => {
  if (!data.article_slug || !data.author_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }

  const currentToken = useToken();
  const path = `${domain}/author/${data.author_slug}/articles/${data.article_slug}/mailing`;
  const res = await handlePutRequest(path, data, currentToken);
  return res as articleAuthorTypes[];
};

const addFile = async (data: addFileTypes): Promise<fileTypes[]> => {
  const currentToken = useToken();
  const path = `${domain}/author/articles/${data.article_slug}/add_files/${data.path}`;
  const res = await handlePutFileRequest(path, data.file, currentToken);
  return res;
};

const addOthersFiles = async (data: addFileTypes): Promise<fileTypes[]> => {
  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.article_slug}/add_files/other`;
  const res = await handlePostFileRequest(path, data.file, currentToken);
  return res as fileTypes[];
};

const getFiles = async (slug: string): Promise<fileTypes[]> => {
  const currentToken = useToken();
  return (await handleGetRequest(
    apiClient,
    `${domain}/author/articles/${slug}/files`,
    currentToken,
  )) as fileTypes[];
};

const getMainFile = async (id: number): Promise<string> => {
  const currentToken = useToken();
  return await handleGetRequest(
    apiClient,
    `${domain}/author/articles/${id}/main_file`,
    currentToken,
  );
};

const addDeclarations = async (
  data: addDeclarationsTypes,
): Promise<SuccessType> => {
  if (!data) {
    throw new Error("Falta datos");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.slug}/responsibility_declarations`;
  const res = await handlePutRequest(path, data.declarations, currentToken);
  return res;
};

const addSuggestedReviewer = async (
  data: addSuggestedReviewerTypes,
): Promise<suggestedReviewerTypes[]> => {
  if (!data) {
    throw new Error("Falta datos");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.article_slug}/suggest_reviewer`;
  const res = await handleRequest(path, data.data, currentToken);
  return res;
};

const editSuggestedReviewer = async (
  data: editSuggestedReviewerTypes,
): Promise<suggestedReviewerTypes[]> => {
  if (!data) {
    throw new Error("Falta datos");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.article_slug}/suggest_reviewer/${data.suggested_reviewer_id}/edit_suggested_reviewer`;

  const { article_slug, suggested_reviewer_id, ...body } = data;

  const res = await handlePatchRequest(path, body, currentToken);
  return res as suggestedReviewerTypes[];
};

const confirmArticle = async (slug: string): Promise<SuccessType> => {
  if (!slug) {
    throw new Error("Falta slug");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${slug}/confirm`;
  const res = await handlePutRequest(path, slug, currentToken);
  return res;
};

const applyChanges = async (slug: string): Promise<SuccessType> => {
  if (!slug) {
    throw new Error("Falta slug");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${slug}/apply_changes`;
  const res = await handlePutRequest(path, slug, currentToken);
  return res;
};

const getSuggestedReviewers = async (
  article_slug: string,
): Promise<suggestedReviewerTypes[]> => {
  const currentToken = useToken();

  const res = await apiClient.get(
    `${domain}/author/articles/${article_slug}/suggest_reviewer`,
    {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    },
  );
  return res.data as suggestedReviewerTypes[];
};

const deleteAuthor = async (author_slug: string): Promise<SuccessType> => {
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.delete(`${domain}/author/${author_slug}/`, {
    headers,
  });
  return res.data as SuccessType;
};

const deleteFile = async (data: deleteFileTypes): Promise<fileTypes[]> => {
  if (!data.article_slug) {
    throw new Error("Falta slug");
  }
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.delete(
    `${domain}/author/articles/${data.article_slug}/files/${data.file_id}`,
    { headers },
  );
  return res.data as fileTypes[];
};

const updateFileVisibility = async (
  data: updateFileVisibilityTypes,
): Promise<SuccessType> => {
  if (!data.article_slug) {
    throw new Error("Falta slug");
  }
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const body = { file_visible_by_reviewer: data.file_visible_by_reviewer };
  const res = await apiClient.patch(
    `${domain}/editor/articles/${data.article_slug}/files/${data.file_id}/visibility`,
    body,
    { headers },
  );
  return res.data as SuccessType;
};

const updateReviewFileVisibility = async (
  data: updateReviewFileVisibilityTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const body = { visible_by_author: data.visible_by_author };
  const res = await apiClient.patch(
    `${domain}/editor/review/${data.review_id}/files/${data.visible_by_author}/visibility`,
    body,
    { headers },
  );
  return res.data as SuccessType;
};

const deleteSuggestedReviewer = async (
  data: any,
): Promise<suggestedReviewerTypes[]> => {
  const currentToken = useToken();

  const url = `${domain}/author/articles/${data.article_slug}/suggest_reviewer/${data.mail}`;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.delete(url, { headers });
  return res.data as suggestedReviewerTypes[];
};

const deleteArticle = async (article_slug: string): Promise<any> => {
  if (!article_slug) {
    throw new Error("Falta slug");
  }
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.delete(
    `${domain}/author/articles/${article_slug}`,
    { headers },
  );
  return res.data;
};

const applyAuthorChanges = async (
  data: applyAuthorChangesTypes,
): Promise<SuccessType> => {
  if (!data) {
    throw new Error("Falta slug");
  }

  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.article_slug}/apply_changes`;
  const res = await handlePutRequest(path, data, currentToken);
  return res as SuccessType;
};

const getVeredictsHistory = async (
  slug: string,
): Promise<veredictsHistoryTypes[]> => {
  const currentToken = useToken();

  const veredicts = await handleGetRequest(
    apiClient,
    `${domain}/author/articles/${slug}/veredicts`,
    currentToken,
  );
  return veredicts as veredictsHistoryTypes[];
};

const editAuthor = async (data: editAuthorTypes): Promise<SuccessType> => {
  const currentToken = useToken();
  const path = `${domain}/author/${data.author_slug}`;
  const res = await handlePatchRequest(path, data.editData, currentToken);
  return res as SuccessType;
};

const removeAuthor = async (
  data: removeAuthorTypes,
): Promise<articleAuthorTypes[]> => {
  const currentToken = useToken();

  const url = `${domain}/author/${data.author_slug}/articles/${data.article_slug}`;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.delete(url, { headers });
  return res.data as articleAuthorTypes[];
};

const setAuthorsOrder = async (
  data: setAuthorsOrderTypes,
): Promise<SuccessType> => {
  if (!data.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const currentToken = useToken();

  const path = `${domain}/author/articles/${data.article_slug}`;
  const res = await handlePutRequest(path, data.data, currentToken);
  return res as SuccessType;
};

const putAuthorContributions = async (
  data: putAuthorContibutionsTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  const path = `${domain}/author/articles/${data.article_slug}/contributions/${data.author_slug}?contributions=${data.contributions}`;
  const res = await handlePutRequest(path, undefined, currentToken);
  return res as SuccessType;
};

const getAuthorHistory = async (
  article_slug: string,
): Promise<authorHistoryTypes> => {
  const currentToken = useToken();
  const author = await handleGetRequest<authorHistoryTypes>(
    apiClient,
    `${domain}/author/articles/${article_slug}/history`,
    currentToken,
  );
  return author;
};

const setContribution = async (data: {
  article_slug: string;
  author_slug: string;
  contribution: number;
}): Promise<SuccessType> => {
  const currentToken = useToken();
  const path = `${domain}/author/${data.author_slug}/articles/${data.article_slug}/contribution/${data.contribution}`;
  const res = await handlePutRequest(path, undefined, currentToken);
  return res as SuccessType;
};

const getArticleAuthors = async (
  slug: string,
): Promise<articleAuthorTypes[]> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("Authentication token not found.");
  }
  const articleAuthors = await handleGetRequest<articleAuthorTypes[]>(
    apiClient,
    `${domain}/articles/${slug}/article_authors`,
    currentToken,
  );
  return articleAuthors;
};

const authorService = {
  createAuthor,
  getAuthors,
  getAuthorMe,
  setMainAuthor,
  addAuthor,
  setMailingAuthor,
  confirmArticle,
  applyChanges,
  addDeclarations,
  addSuggestedReviewer,
  getMyArticles,
  getFiles,
  getMainFile,
  addFile,
  addOthersFiles,
  sendArticle,
  getSuggestedReviewers,
  deleteAuthor,
  deleteFile,
  updateFileVisibility,
  deleteSuggestedReviewer,
  deleteArticle,
  applyAuthorChanges,
  getVeredictsHistory,
  editAuthor,
  removeAuthor,
  setAuthorsOrder,
  putAuthorContributions,
  getAuthorHistory,
  updateReviewFileVisibility,
  setContribution,
  editSuggestedReviewer,
  getArticleAuthors,
};

export default authorService;
